/**
 * Demo Mode — deterministic client-side anonymization engine.
 *
 * Every API response body in this app is parsed at exactly ONE place
 * (api/client.js `request()`), which makes it possible to anonymize all live
 * customer data on the fly. This module owns that transform.
 *
 * Design rules:
 *   • DETERMINISTIC — the same real value always yields the same fake value
 *     (FNV-1a hash of the lowercased value → index into a curated pool), so
 *     grouping, joins (tenant name ↔ row), filters and baseline-vs-event
 *     comparisons all keep working.
 *   • REVERSIBLE WHERE IT MATTERS — masked/fake values get reused as backend
 *     request keys (`?tenantId=`, `?ip=`, `/{mspCode}/options`, UPNs in POST
 *     bodies). A session key store maps fake → real, and `restoreEndpoint()` /
 *     `restoreBody()` put the real values back on the wire so live pivots keep
 *     working while the screen shows only anonymized data.
 *   • IDs THAT AREN'T IDENTIFYING ARE LEFT ALONE — alert/rule/event/device/app
 *     GUIDs, row keys, dates, operations, statuses, geo and rule names all pass
 *     through untouched (they're meaningless to a prospect and they're needed
 *     for follow-up requests).
 *
 * Tenant GUIDs and IPs are PARTIALLY MASKED rather than replaced with fakes
 * (`752b8092-****-****-****-************`, `**.**.45.67`) — the presenter can
 * still talk about "this IP" without the identifying part being visible.
 *
 * Client-side only: the real data still transits the browser (visible in the
 * Network tab). That is an accepted risk for presenter-driven screen shares.
 */

import ipaddr from 'ipaddr.js';

// ---------------------------------------------------------------------------
// Environment flags
// ---------------------------------------------------------------------------

function envFlag(name) {
  try {
    return import.meta.env?.[name];
  } catch {
    return undefined;
  }
}

// Leak assertions run on the Vite dev server AND on the deployed DEV site, so
// the verification walk surfaces missed fields as console warnings. Never in prod.
const LEAK_CHECK = !!envFlag('DEV') || envFlag('VITE_ENVIRONMENT') === 'dev';

// ---------------------------------------------------------------------------
// Hashing / small helpers
// ---------------------------------------------------------------------------

function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

const norm = (v) => String(v ?? '').trim().toLowerCase();
const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function pick(pool, seed, attempt) {
  return pool[fnv1a(`${seed}#${attempt}`) % pool.length];
}

function hashToken(seed, attempt, len = 5) {
  return fnv1a(`t:${seed}#${attempt}`).toString(36).toUpperCase().padStart(len, '0').slice(-len);
}

// ---------------------------------------------------------------------------
// Pools — Microsoft-fictional companies plus neutral person/device/subject names
// ---------------------------------------------------------------------------

// Every fake COMPANY NAME carries a trailing "Demo" and every fake DOMAIN sits on
// the `.demo` TLD, which is not a real one. Fictional names alone read as real
// clients to a prospect ("Fourth Coffee", "reid.chandler@fourthcoffee.com" are
// indistinguishable from a customer), and a demo must never leave anyone wondering
// whose data is on screen. Both marks are applied at generation time (here and in
// fakeTenantName / fakeMspName), NOT baked into the pools, so the pool entries stay
// usable for deriving short names and domain labels.
const DEMO_LABEL = 'Demo';

// Pool entries carry the bare label; the TLD is
// always appended here.
const DEMO_TLD = '.demo';

const COMPANIES = [
  ['Contoso Ltd', 'contoso'],
  ['Fabrikam Inc', 'fabrikam'],
  ['Northwind Traders', 'northwindtraders'],
  ['Woodgrove Bank', 'woodgrovebank'],
  ['Adventure Works', 'adventure-works'],
  ['Alpine Ski House', 'alpineskihouse'],
  ['Blue Yonder Airlines', 'blueyonderairlines'],
  ['City Power and Light', 'citypowerlight'],
  ['Coho Vineyard', 'cohovineyard'],
  ['Consolidated Messenger', 'consolidatedmessenger'],
  ['Fourth Coffee', 'fourthcoffee'],
  ['Graphic Design Institute', 'graphicdesigninstitute'],
  ['Humongous Insurance', 'humongousinsurance'],
  ['Litware Inc', 'litwareinc'],
  ['Lucerne Publishing', 'lucernepublishing'],
  ['Margies Travel', 'margiestravel'],
  ['Nod Publishers', 'nodpublishers'],
  ['Proseware Inc', 'proseware'],
  ['Relecloud', 'relecloud'],
  ['School of Fine Art', 'schooloffineart'],
  ['Southridge Video', 'southridgevideo'],
  ['Tailspin Toys', 'tailspintoys'],
  ['The Phone Company', 'thephone-company'],
  ['Trey Research', 'treyresearch'],
  ['VanArsdel Ltd', 'vanarsdelltd'],
  ['Wide World Importers', 'wideworldimporters'],
  ['Wingtip Toys', 'wingtiptoys'],
  ['A Datum Corporation', 'adatum'],
  ['Bellows College', 'bellowscollege'],
  ['Best For You Organics', 'bestforyouorganics'],
  ['First Up Consultants', 'firstupconsultants'],
  ['Lamna Healthcare', 'lamnahealthcare'],
  ['Liberty Bakery', 'libertybakery'],
  ['Munsons Pickles', 'munsonspickles'],
  ['Northwind Electric Cars', 'northwindelectriccars'],
  ['Tailwind Traders', 'tailwindtraders'],
  ['Fincastle Group', 'fincastlegroup'],
  ['Cronus Energy', 'cronusenergy'],
  ['Ridgeview Partners', 'ridgeviewpartners'],
  ['Harborview Clinic', 'harborviewclinic'],
];

const FIRST_NAMES = [
  'Alex', 'Avery', 'Bailey', 'Bianca', 'Brandon', 'Brooke', 'Caleb', 'Camila', 'Carter', 'Cassie',
  'Cole', 'Dana', 'Dean', 'Delia', 'Derek', 'Diana', 'Dylan', 'Elena', 'Eli', 'Emma',
  'Ethan', 'Fiona', 'Gabriel', 'Grace', 'Hannah', 'Harold', 'Hugo', 'Iris', 'Isaac', 'Ivy',
  'Jared', 'Jasmine', 'Jonah', 'Julia', 'Kaitlyn', 'Kevin', 'Lara', 'Leo', 'Lila', 'Logan',
  'Maya', 'Micah', 'Nadia', 'Nathan', 'Nina', 'Oliver', 'Paige', 'Peter', 'Quinn', 'Rachel',
  'Reid', 'Rosa', 'Ryan', 'Sadie', 'Simon', 'Tessa', 'Theo', 'Vera', 'Wesley', 'Zoe',
];

const LAST_NAMES = [
  'Abbott', 'Alvarez', 'Bennett', 'Blackwood', 'Boyer', 'Brennan', 'Calderon', 'Carrington', 'Chandler', 'Colton',
  'Dalton', 'Delgado', 'Donovan', 'Ellsworth', 'Everett', 'Fairbanks', 'Fenwick', 'Fletcher', 'Gallagher', 'Garrison',
  'Grafton', 'Halloran', 'Hartley', 'Hawkins', 'Holbrook', 'Ingram', 'Jennings', 'Kendall', 'Kirkland', 'Langley',
  'Lockwood', 'Marlowe', 'Mercer', 'Middleton', 'Nolan', 'Norwood', 'Oakley', 'Pemberton', 'Prescott', 'Quimby',
  'Radford', 'Ramsey', 'Renfrew', 'Ridgeway', 'Sandoval', 'Sherwood', 'Sinclair', 'Stanton', 'Thatcher', 'Thornton',
  'Underwood', 'Vaughan', 'Wakefield', 'Waverly', 'Whitfield', 'Winslow', 'Woodard', 'Yardley', 'Youngblood', 'Zimmerman',
];

const SUBJECTS = [
  'Invoice for last month', 'Quarterly report — draft', 'Please review the attached', 'Updated schedule for next week',
  'Payment remittance advice', 'Action required: document signature', 'Meeting notes and follow-ups',
  'Purchase order confirmation', 'Shared file: budget worksheet', 'Vendor statement attached',
  'Re: contract renewal', 'Fwd: shipping confirmation', 'Weekly status update', 'New voicemail message',
  'Password expiry notification', 'Team offsite planning', 'Expense report submitted', 'Client onboarding checklist',
  'Timesheet reminder', 'Policy acknowledgement required', 'Support ticket updated', 'Your order has shipped',
  'Benefits enrollment window', 'Scanned document from copier', 'Project kickoff agenda', 'Reconciliation summary',
  'Updated contact list', 'Holiday coverage schedule', 'Training completion certificate', 'Monthly newsletter',
];

const DEVICE_PREFIXES = ['DESKTOP', 'LAPTOP', 'WIN11', 'WKS', 'MACBOOK', 'SRV'];

const GROUP_NAMES = [
  'SG-All-Staff', 'SG-Finance-Users', 'SG-Sales-Team', 'SG-Field-Technicians', 'SG-Executive-Team',
  'SG-MFA-Exempt-Review', 'SG-Contractors', 'SG-Helpdesk-Operators', 'SG-Remote-Workers', 'SG-Clinical-Staff',
  'SG-Warehouse-Users', 'SG-Legal-Review', 'SG-Marketing-Team', 'SG-Interns', 'SG-Service-Accounts',
  'SG-CA-Exception-Group', 'SG-Licensed-E3', 'SG-Licensed-E5', 'SG-Shared-Mailbox-Access', 'SG-Branch-Office',
];

const APP_NAMES = [
  'Timesheet Portal', 'Fleet Tracker', 'Expense Manager', 'Document Vault', 'Field Service App',
  'Payroll Connector', 'CRM Sync Agent', 'Inventory Console', 'Scheduling Assistant', 'Invoice Importer',
  'Reporting Gateway', 'Signature Service', 'Backup Reporter', 'Print Manager', 'Survey Collector',
];

const MSPS = [
  ['NGT', 'Northgate Technology'],
  ['SLVR', 'Silverline IT'],
  ['BRCR', 'Bridgecrest Managed IT'],
  ['OAKH', 'Oakhaven Technologies'],
  ['PNCL', 'Pinnacle IT Partners'],
  ['SUMT', 'Summit Data Systems'],
  ['CLRW', 'Clearwater Managed Services'],
  ['IRNB', 'Ironbridge Technology'],
  ['RDST', 'Redstone IT Group'],
  ['HRBP', 'Harbor Point Technology'],
  ['KSTN', 'Keystone Network Services'],
  ['LNDM', 'Landmark IT'],
  ['MRDN', 'Meridian Managed IT'],
  ['NRTS', 'Northstar Tech Solutions'],
  ['TRLB', 'Trailblaze IT'],
  ['VNTG', 'Vantage Point IT'],
  ['WSTF', 'Westfield Technology'],
  ['ZNTH', 'Zenith Managed IT'],
  ['EVRG', 'Evergreen IT Partners'],
  ['FRPT', 'Fairpoint Technology'],
];

// Microsoft first-party / platform app names must stay readable — the demo loses
// all meaning if "Microsoft Graph" becomes "Fleet Tracker".
const MS_APP_RE = /^(microsoft|office|azure|windows|exchange|sharepoint|onedrive|teams|outlook|skype|power\s?(bi|apps|automate)|dynamics|defender|intune|entra|graph|substrate|m365|o365|sway|yammer|viva|visual studio|onenote|bing|copilot)/i;

// ---------------------------------------------------------------------------
// Key store — forward (real → fake) and reverse (fake → real) maps
// ---------------------------------------------------------------------------

const forward = new Map();   // category -> Map(realNorm -> fake)
const reverse = new Map();   // category -> Map(fakeNorm -> realTrimmed)
const ipReverse = new Map(); // maskedNorm -> Set(realTrimmed)   (IP masking is lossy)

const companyIdx = new Map(); // company token -> pool index
const idxOwner = new Map();   // pool index -> owning token
const mspIdx = new Map();     // msp token -> pool index
const mspIdxOwner = new Map();

// Bumped on every new mapping so cached restore plans rebuild lazily.
let storeVersion = 0;
// Bumped ONLY when a category used by the free-text scrubber changes. Walking a
// large payload mints thousands of person/UPN/device mappings; keying the
// free-text regex on storeVersion would recompile it once per mapping.
let freetextVersion = 0;

function fwd(cat) {
  let m = forward.get(cat);
  if (!m) { m = new Map(); forward.set(cat, m); }
  return m;
}

function rev(cat) {
  let m = reverse.get(cat);
  if (!m) { m = new Map(); reverse.set(cat, m); }
  return m;
}

// Persisted maps (all small):
//   • tenant / msp — needed for OUTBOUND restore, so deep links and MSP-keyed
//     reads survive a reload or a new tab.
//   • company / domain / domainlabel — needed for FREE-TEXT scrubbing: a page
//     that never loads the tenant list (e.g. the Activity Log) would otherwise
//     have an empty known-value set and could render a real client name sitting
//     inside a free-text summary or note.
// IP / UPN / person maps stay in memory: they're derived deterministically from
// the value itself, and a pivot is always launched from a row loaded in the same
// page load — which is exactly what populates the reverse map it needs.
const PERSISTED = {
  tenant: 'demoTenantMap',
  msp: 'demoMspMap',
  company: 'demoCompanyMap',
  domain: 'demoDomainMap',
  domainlabel: 'demoDomainLabelMap',
};

// Bump whenever the SHAPE of a generated fake value changes (`.demo` TLD in
// v1.134.1, "Demo"-suffixed company + MSP names in v1.134.2, faked file names /
// attachment lists / mail folder paths in v1.151.0). A stored map would
// otherwise keep handing back the old-shape fake for every value already mapped in
// that browser, and the change would look like it hadn't deployed. Dropping the
// maps is safe: masks/fakes are re-derived deterministically from the real value,
// and the reverse maps that outbound restore needs repopulate as soon as the page
// loads data.
const STORE_SCHEMA = '4';
const STORE_SCHEMA_KEY = 'demoMapSchema';

function loadPersisted() {
  let stale = false;
  try {
    stale = localStorage.getItem(STORE_SCHEMA_KEY) !== STORE_SCHEMA;
  } catch { stale = false; }

  for (const [cat, storeKey] of Object.entries(PERSISTED)) {
    try {
      if (stale) { localStorage.removeItem(storeKey); continue; }
      const raw = localStorage.getItem(storeKey);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      // Stored as [[fake, real], …] so BOTH sides keep their original casing.
      // (An object keyed by the fake would lowercase it — map keys are normalized —
      // and "Fabrikam Inc Demo" / "EVRG" would come back lowercased after a reload.)
      const pairs = Array.isArray(parsed) ? parsed : (isPlainObject(parsed) ? Object.entries(parsed) : []);
      for (const pair of pairs) {
        if (!Array.isArray(pair)) continue;
        const [fake, real] = pair;
        if (typeof fake !== 'string' || typeof real !== 'string') continue;
        rev(cat).set(norm(fake), real);
        fwd(cat).set(norm(real), fake);
      }
    } catch { /* corrupt / unavailable — rebuilt as data loads */ }
  }

  if (stale) {
    try { localStorage.setItem(STORE_SCHEMA_KEY, STORE_SCHEMA); } catch { /* ignore */ }
  }
  storeVersion++;
}
loadPersisted();

function persist(cat) {
  const storeKey = PERSISTED[cat];
  if (!storeKey) return;
  try {
    // Pairs of the ORIGINAL-cased fake + original-cased real. fwd() holds the cased
    // fake (keyed by normalized real); rev() holds the cased real (keyed by
    // normalized fake) — so both are needed to reconstruct a lossless pair.
    const pairs = [];
    for (const fake of fwd(cat).values()) {
      const real = rev(cat).get(norm(fake));
      if (real !== undefined) pairs.push([fake, real]);
    }
    localStorage.setItem(storeKey, JSON.stringify(pairs));
  } catch { /* storage full / disabled — restore still works in-memory */ }
}

/**
 * Core memoized mapper. `gen(realNorm, attempt)` produces a candidate fake;
 * attempts increase until the candidate isn't already owned by a DIFFERENT real
 * value, so two real values never collapse into one fake.
 */
function mapValue(cat, real, gen) {
  const realStr = String(real).trim();
  const key = realStr.toLowerCase();
  if (!key) return realStr;

  const f = fwd(cat);
  const hit = f.get(key);
  if (hit !== undefined) return hit;

  const r = rev(cat);
  let fake = gen(key, 0);
  for (let n = 1; n < 64; n++) {
    const owner = r.get(norm(fake));
    if (owner === undefined || norm(owner) === key) break;
    fake = gen(key, n);
  }
  f.set(key, fake);
  r.set(norm(fake), realStr);
  storeVersion++;
  if (FREETEXT_CATS.includes(cat)) freetextVersion++;
  if (PERSISTED[cat]) persist(cat);
  return fake;
}

// ---------------------------------------------------------------------------
// Company index — tenant name ↔ domain ↔ short name share one pool entry
// ---------------------------------------------------------------------------

const nameToken = (n) => `n:${norm(n)}`;

// Country-code second-level domains, so `client.co.uk` reduces to `client` and not
// to `co` (which would merge every .co.uk client into one fictional company).
const CC_SLD_RE = /\.(co|com|net|org|gov|edu|ac|or|ne)\.[a-z]{2}$/;

/**
 * Reduce a domain to the company it identifies, so every domain a tenant owns maps
 * to ONE fictional company: `client.com`, `client.onmicrosoft.com` and
 * `mail.client.com` all yield `d:client`.
 */
const domainBaseToken = (d) => {
  const stripped = norm(d)
    .replace(/^www\./, '')
    .replace(/\.onmicrosoft\.com$/, '')
    .replace(CC_SLD_RE, '')
    .replace(/\.[a-z]{2,}$/, '');
  const labels = stripped.split('.').filter(Boolean);
  return `d:${labels[labels.length - 1] || stripped}`;
};

function freshIndex(token, poolLen, idxMap, ownerMap) {
  let idx = fnv1a(token) % poolLen;
  for (let n = 0; n < poolLen; n++) {
    const owner = ownerMap.get(idx);
    if (owner === undefined || owner === token) break;
    idx = (idx + 1) % poolLen;
  }
  if (ownerMap.get(idx) === undefined) ownerMap.set(idx, token);
  idxMap.set(token, idx);
  storeVersion++;
  return idx;
}

/**
 * Resolve ONE shared pool index for a set of related tokens (tenant name ↔ domain,
 * MSP code ↔ MSP name), so every identifier a client owns renders as one fictional
 * company. When nothing is mapped yet the index is seeded from the SMALLEST token
 * rather than the first one supplied — otherwise the pool entry picked would depend
 * on JSON key order, which differs between endpoints for the same real value.
 */
function linkedIndex(tokens, poolLen, idxMap, ownerMap) {
  const present = tokens.filter(Boolean);
  if (present.length === 0) return 0;
  let idx;
  for (const t of present) {
    if (idxMap.has(t)) { idx = idxMap.get(t); break; }
  }
  if (idx === undefined) {
    const seed = present.slice().sort()[0];
    idx = freshIndex(seed, poolLen, idxMap, ownerMap);
  }
  for (const t of present) if (!idxMap.has(t)) idxMap.set(t, idx);
  return idx;
}

/**
 * Rebuild the pool-index maps from persisted forward maps on load. Without this a
 * reload starts with empty index maps, so a domain first seen AFTER the reload would
 * pick a fresh pool entry and render the same client as two different fictional
 * companies (its name says one, its `.onmicrosoft` domain says another).
 * `longestPrefixIndex` recovers the entry from the stored fake value, which always
 * starts with that entry's name/label.
 */
function longestPrefixIndex(fake, pool, field) {
  const f = norm(fake);
  let best = -1;
  let bestLen = 0;
  for (let i = 0; i < pool.length; i++) {
    const candidate = norm(pool[i][field]);
    if (candidate && f.startsWith(candidate) && candidate.length > bestLen) {
      best = i;
      bestLen = candidate.length;
    }
  }
  return best;
}

function rebuildPoolIndexes() {
  const seed = (cat, tokenFor, pool, field, idxMap, ownerMap, transform) => {
    for (const [realNorm, fake] of fwd(cat)) {
      const idx = longestPrefixIndex(transform ? transform(fake) : fake, pool, field);
      if (idx < 0) continue;
      const token = tokenFor(realNorm);
      if (!idxMap.has(token)) idxMap.set(token, idx);
      if (ownerMap.get(idx) === undefined) ownerMap.set(idx, token);
    }
  };
  seed('company', nameToken, COMPANIES, 0, companyIdx, idxOwner);
  seed('domain', domainBaseToken, COMPANIES, 1, companyIdx, idxOwner, (f) => norm(f).split('.')[0]);
  seed('msp', (r) => `mc:${r}`, MSPS, 0, mspIdx, mspIdxOwner);
  seed('mspname', (r) => `mn:${r}`, MSPS, 1, mspIdx, mspIdxOwner);
}

// Runs here, not next to loadPersisted(): it needs nameToken / domainBaseToken,
// which are defined above this point but below the persisted-map load.
rebuildPoolIndexes();

/**
 * Cross-seed: a row carrying a company name AND a domain (tenant lists, BPC
 * rows) maps both to the SAME fictional company, so "Cohen USA" and
 * "cohenusa.com" stay consistent wherever either appears later.
 */
function companyIndexForRow(obj, ownTokenFirst) {
  const tokens = [];
  if (ownTokenFirst) tokens.push(ownTokenFirst);
  if (obj) {
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (typeof v !== 'string' || !v.trim()) continue;
      const nk = normKey(k);
      if (COMPANY_KEYS.has(nk)) tokens.push(nameToken(v));
      else if (DOMAIN_KEYS.has(nk)) tokens.push(domainBaseToken(v));
    }
  }
  return linkedIndex(tokens, COMPANIES.length, companyIdx, idxOwner);
}

function mspIndexForRow(obj, ownToken) {
  const tokens = [ownToken];
  if (obj) {
    // On an MSP row the display name IS the MSP name (`{ code, displayName }` from
    // MSP_CATALOG, `{ mspCode, displayName }` from the config endpoints). Without
    // this, the code and the name link to different pool entries and render as two
    // unrelated MSPs — and the row's first-processed key mutates its sibling, so
    // the pairing can't be recovered afterwards.
    const isMspRow = Object.keys(obj).some((k) => {
      const nk = normKey(k);
      return nk === 'code' || nk === 'mspcode' || nk === 'msp';
    });
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (typeof v !== 'string' || !v.trim()) continue;
      const nk = normKey(k);
      if (MSP_CODE_KEYS.has(nk) || nk === 'code') tokens.push(`mc:${norm(v)}`);
      else if (MSP_NAME_KEYS.has(nk) || (isMspRow && nk === 'displayname')) tokens.push(`mn:${norm(v)}`);
    }
  }
  return linkedIndex(tokens, MSPS.length, mspIdx, mspIdxOwner);
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_ANCHORED = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
const EMAIL_GLOBAL = /[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const IPV4_ANCHORED = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
// Free-text IPv4 — rejects matches glued to `/` or `.` so version strings
// ("Chrome/124.0.0.0", "1.2.3.4.5") are left alone.
const IPV4_FREE = /(^|[^\w./])(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?![\d.\w/])/g;
// Broad IPv6 candidate; every hit is validated with ipaddr.js before masking, so
// timestamps (12:34:56) and MAC addresses fall out naturally.
const IPV6_FREE = /[0-9A-Fa-f]{0,4}(?::[0-9A-Fa-f]{0,4}){2,7}(?:\.\d{1,3}){0,3}/g;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
const SID_RE = /^S-\d-\d/i;
const WORDISH = /[A-Za-z0-9_]/;

function validOctets(s) {
  const p = s.split('.');
  if (p.length !== 4) return false;
  return p.every((o) => o.length > 0 && o.length <= 3 && Number(o) <= 255);
}

// ---------------------------------------------------------------------------
// Public transformers
// ---------------------------------------------------------------------------

/**
 * Company / client / tenant friendly name → fictional company name, always ending
 * in "Demo" (`Fourth Coffee Demo`) so a tenant list can never be mistaken for a
 * real client roster.
 */
export function fakeTenantName(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const base = COMPANIES[companyIndexForRow(obj, nameToken(value))][0];
  return mapValue('company', value, (key, n) =>
    `${base}${n === 0 ? '' : ` ${hashToken(key, n, 2)}`} ${DEMO_LABEL}`);
}

/**
 * Email domain → the fictional company's domain, always on the non-real `.demo`
 * TLD (`contoso.demo`). The `.onmicrosoft` marker is preserved for tenant initial
 * domains, but its TLD is swapped too (`contoso.onmicrosoft.demo`) — a real-looking
 * TLD anywhere invites a prospect to read the screen as live customer data.
 */
export function fakeDomain(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const raw = value.trim();
  const isOnMs = /\.onmicrosoft\.com$/i.test(raw);
  const idx = companyIndexForRow(obj, domainBaseToken(raw));
  const fake = mapValue('domain', raw, (key, n) => {
    const label = COMPANIES[idx][1];
    const suffixed = n === 0 ? label : `${label}${hashToken(key, n, 2).toLowerCase()}`;
    return `${suffixed}${isOnMs ? '.onmicrosoft' : ''}${DEMO_TLD}`;
  });
  // Also register the bare domain LABEL so free text catches host names built
  // from it (e.g. "cohenusa.sharepoint.com", "COHENUSA\\user").
  const realLabel = norm(raw).replace(/^www\./, '').split('.')[0];
  if (realLabel && realLabel.length >= 5 && !fwd('domainlabel').has(realLabel)) {
    const fakeLabel = norm(fake).split('.')[0];
    fwd('domainlabel').set(realLabel, fakeLabel);
    rev('domainlabel').set(fakeLabel, realLabel);
    storeVersion++;
    freetextVersion++;
    persist('domainlabel');
  }
  return fake;
}

/** Analyst-facing tenant short code → code derived from the fake company. */
export function fakeShortName(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const idx = companyIndexForRow(obj, null);
  return mapValue('shortname', value, (key, n) => {
    const base = COMPANIES[idx][0].replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 5) || 'DEMO';
    return n === 0 ? base : `${base}${hashToken(key, n, 1)}`;
  });
}

/** Email address / UPN → `first.last@<fake company domain>`. Wildcards kept. */
export function fakeUpn(value, obj) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!raw || !EMAIL_ANCHORED.test(raw)) return value;
  const at = raw.lastIndexOf('@');
  const local = raw.slice(0, at);
  const fakeDom = fakeDomain(raw.slice(at + 1), obj);
  if (local === '*') return `*@${fakeDom}`;
  return mapValue('upn', raw, (key, n) => {
    const first = pick(FIRST_NAMES, `f:${key}`, n).toLowerCase();
    const last = pick(LAST_NAMES, `l:${key}`, n).toLowerCase();
    const tail = n === 0 ? '' : hashToken(key, n, 2).toLowerCase();
    return `${first}.${last}${tail}@${fakeDom}`;
  });
}

/** Person display name → `First Last`. Email-shaped input routes to fakeUpn. */
export function fakePersonName(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const raw = value.trim();
  if (EMAIL_ANCHORED.test(raw)) return fakeUpn(raw, obj);
  return mapValue('person', raw, (key, n) =>
    `${pick(FIRST_NAMES, `pf:${key}`, n)} ${pick(LAST_NAMES, `pl:${key}`, n)}${n === 0 ? '' : ` ${hashToken(key, n, 1)}`}`);
}

/** Device / host name → `DESKTOP-XXXXXX` style name. */
export function fakeDeviceName(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  return mapValue('device', value, (key, n) =>
    `${pick(DEVICE_PREFIXES, `dp:${key}`, 0)}-${hashToken(key, n, 6)}`);
}

/** Email subject → plausible generic subject. */
export function fakeSubject(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  return mapValue('subject', value, (key, n) =>
    (n === 0 ? pick(SUBJECTS, `s:${key}`, 0) : `${pick(SUBJECTS, `s:${key}`, 0)} (${hashToken(key, n, 2)})`));
}

/** Entra security-group display name → plausible generic group name. */
export function fakeGroupName(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  return mapValue('group', value, (key, n) =>
    (n === 0 ? pick(GROUP_NAMES, `g:${key}`, 0) : `${pick(GROUP_NAMES, `g:${key}`, 0)}-${hashToken(key, n, 2)}`));
}

const FILE_STEMS = [
  'Quarterly Report', 'Budget Worksheet', 'Invoice', 'Statement of Work', 'Meeting Notes',
  'Project Plan', 'Expense Summary', 'Onboarding Checklist', 'Vendor Agreement', 'Timesheet',
  'Policy Document', 'Training Deck', 'Scanned Document', 'Purchase Order', 'Reconciliation',
  'Contact List', 'Coverage Schedule', 'Audit Summary', 'Price List', 'Proposal Draft',
];

/** File / attachment name -> plausible generic name, extension preserved.
 *
 *  The extension is kept because it is not identifying and it is load-bearing for
 *  the reader: a demo that turns `INVOICE_2982.pdf` into a name with no extension
 *  stops looking like a document. Sizes in an attachment list are preserved for the
 *  same reason (see fakeAttachmentList). */
export function fakeFileName(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const raw = value.trim();
  const dot = raw.lastIndexOf('.');
  const ext = (dot > 0 && dot > raw.length - 8) ? raw.slice(dot) : '';
  return mapValue('file', raw, (key, n) =>
    `${pick(FILE_STEMS, `fs:${key}`, 0)}${n === 0 ? '' : ` ${hashToken(key, n, 2)}`}${ext}`);
}

/** `name (NNNNb); name (NNNNb)` -> each name faked, the `(NNNNb)` shape preserved.
 *  Falls back to faking the whole string as one name when the list does not parse,
 *  which is the safe direction: an unparseable list must not pass through. */
export function fakeAttachmentList(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const segs = value.split('; ');
  const out = [];
  for (const seg of segs) {
    const m = /^(.*?)(\s*\(\d+b\))$/.exec(seg);
    if (!m) return fakeFileName(value);
    out.push(`${fakeFileName(m[1])}${m[2]}`);
  }
  return out.join('; ');
}

/** Mail folder path -> the well-known segments kept, customer-named ones faked.
 *  `\Inbox\THE LANDINGS\Attorneys` -> `\Inbox\SG-Legal-Review\...`-style. A nested
 *  path carries client / matter names (54% of Calendar rows are nested), which is
 *  the same reasoning that keeps the full path out of the one-line description. */
const WELL_KNOWN_FOLDERS = new Set([
  'inbox', 'drafts', 'sent items', 'deleted items', 'junk email', 'archive', 'calendar',
  'contacts', 'notes', 'tasks', 'outbox', 'conversation history', 'sync issues',
  'recoverable items', 'conflicts', 'local failures', 'server failures', 'purges', 'versions',
]);
export function fakeFolderPath(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const raw = value.trim();
  const lead = raw.startsWith('\\') ? '\\' : '';
  const parts = raw.split('\\').filter(Boolean);
  if (parts.length === 0) return value;
  const mapped = parts.map((seg) =>
    (WELL_KNOWN_FOLDERS.has(seg.toLowerCase()) ? seg : fakeFolderSegment(seg)));
  return `${lead}${mapped.join('\\')}`;
}

const FOLDER_SEGMENTS = [
  'Client Matters', 'Projects', 'Accounts', 'Casework', 'Vendors', 'Contracts',
  'Regional', 'Archive 2025', 'Team', 'Escalations', 'Renewals', 'Intake',
];
function fakeFolderSegment(value) {
  return mapValue('folderseg', value, (key, n) =>
    `${pick(FOLDER_SEGMENTS, `fg:${key}`, 0)}${n === 0 ? '' : ` ${hashToken(key, n, 2)}`}`);
}

/** Third-party app display name. Microsoft first-party names pass through. */
export function fakeAppName(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  if (MS_APP_RE.test(value.trim())) return value;
  return mapValue('app', value, (key, n) =>
    (n === 0 ? pick(APP_NAMES, `a:${key}`, 0) : `${pick(APP_NAMES, `a:${key}`, 0)} ${hashToken(key, n, 2)}`));
}

/** MSP short code (PFIT / LCT …) → fake code. Restored on outbound requests. */
export function fakeMspCode(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const raw = value.trim();
  if (raw === '*') return raw;
  const idx = mspIndexForRow(obj, `mc:${norm(raw)}`);
  return mapValue('msp', raw, (key, n) =>
    (n === 0 ? MSPS[idx][0] : `${MSPS[idx][0]}${hashToken(key, n, 1)}`).toUpperCase());
}

/**
 * MSP display name → fake MSP name (paired with the fake code), also carrying the
 * "Demo" suffix — an MSP name is a company name, and "Northgate Technology" reads
 * exactly as plausibly as a real partner.
 */
export function fakeMspName(value, obj) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const base = MSPS[mspIndexForRow(obj, `mn:${norm(value)}`)][1];
  return mapValue('mspname', value, (key, n) =>
    `${base}${n === 0 ? '' : ` ${hashToken(key, n, 2)}`} ${DEMO_LABEL}`);
}

// Case IDs are `IR-{YYYYMMDD}-{UPN}-{NNN}`. Scrubbing them as free text would
// work but the email regex greedily swallows the `IR-20260223-` prefix (a local
// part may legally contain digits and hyphens), so the id shape is parsed here
// and only the UPN segment is replaced. The fake UPN token inside the id is what
// lets restoreEndpoint() turn `/api/cases/{caseId}` back into the real id.
const CASE_ID_RE = /^(IR-\d{8}-)(.+?)(-\d{1,4})$/;

export function fakeCaseId(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  const m = CASE_ID_RE.exec(value.trim());
  if (!m) return scrubFreeText(value);
  const mid = m[2];
  return `${m[1]}${EMAIL_ANCHORED.test(mid) ? fakeUpn(mid) : scrubFreeText(mid)}${m[3]}`;
}

export function fakePhone(value) {
  if (typeof value !== 'string' || !value.trim()) return value;
  return mapValue('phone', value, (key, n) => {
    const d = fnv1a(`p:${key}#${n}`).toString().padStart(8, '0');
    return `+1 (555) 01${d.slice(0, 2)}-${d.slice(2, 6)}`;
  });
}

/**
 * Tenant GUID → visible partial mask keeping the first 8 hex characters:
 *   752b8092-f529-4eff-a6e8-179e3309ec1e → 752b8092-****-****-****-************
 * Non-GUID values pass through. The real GUID is restored on outbound requests.
 */
export function maskTenantGuid(value) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!GUID_RE.test(raw)) return value;
  return mapValue('tenant', raw, (key, n) => {
    const extra = Math.min(n, 4);
    const grp2 = key.slice(9, 9 + extra) + '*'.repeat(4 - extra);
    return `${key.slice(0, 8)}-${grp2}-****-****-************`;
  });
}

function registerIp(masked, real) {
  fwd('ip').set(norm(real), masked);
  const k = norm(masked);
  let set = ipReverse.get(k);
  if (!set) { set = new Set(); ipReverse.set(k, set); }
  set.add(String(real).trim());
  storeVersion++;
}

function maskIpCore(s) {
  const raw = String(s).trim();
  if (!raw) return null;
  const cached = fwd('ip').get(norm(raw));
  if (cached !== undefined) return cached;

  if (IPV4_ANCHORED.test(raw) && validOctets(raw)) {
    const p = raw.split('.');
    const masked = `**.**.${p[2]}.${p[3]}`;
    registerIp(masked, raw);
    return masked;
  }

  if (raw.includes(':')) {
    if (!/[0-9A-Fa-f]/.test(raw)) return null;
    let parsed;
    try { parsed = ipaddr.parse(raw); } catch { return null; }
    if (parsed.kind() !== 'ipv6') return null;
    try {
      if (typeof parsed.isIPv4MappedAddress === 'function' && parsed.isIPv4MappedAddress()) {
        const p = parsed.toIPv4Address().toString().split('.');
        const masked = `::ffff:**.**.${p[2]}.${p[3]}`;
        registerIp(masked, raw);
        return masked;
      }
    } catch { /* fall through to hextet masking */ }
    const groups = parsed.toNormalizedString().split(':');
    if (groups.length !== 8) return null;
    const masked = ['****', '****', '****', '****', ...groups.slice(4)].join(':');
    registerIp(masked, raw);
    return masked;
  }

  return null;
}

/**
 * IP → visible partial mask. IPv4 hides the first two octets; IPv6 hides the
 * identifying first four hextets (the /64 network). Handles `ip:port`,
 * `[ipv6]:port`, compressed IPv6 and IPv4-mapped IPv6. Non-IPs pass through.
 */
export function maskIp(value) {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!raw) return value;

  let m = /^\[([^\]]+)\](?::(\d+))?$/.exec(raw);
  if (m) {
    const inner = maskIpCore(m[1]);
    return inner === null ? value : `[${inner}]${m[2] ? `:${m[2]}` : ''}`;
  }
  m = /^(\d{1,3}(?:\.\d{1,3}){3}):(\d+)$/.exec(raw);
  if (m) {
    const inner = maskIpCore(m[1]);
    return inner === null ? value : `${inner}:${m[2]}`;
  }
  const core = maskIpCore(raw);
  return core === null ? value : core;
}

/** Apply a transformer across a comma-separated list, preserving spacing. */
function mapCsv(value, fn, obj) {
  if (typeof value !== 'string') return value;
  if (!value.includes(',')) return fn(value, obj);
  return value.split(',').map((part) => {
    const t = part.trim();
    if (!t) return part;
    return part.replace(t, fn(t, obj));
  }).join(',');
}

// ---------------------------------------------------------------------------
// Free-text scrubbing
// ---------------------------------------------------------------------------

// Real values safe to search for inside arbitrary strings. Deliberately small
// and slow-changing (companies, domains + their bare labels, tenant GUIDs, MSP
// names) so the combined regex is cheap to rebuild. Real emails are covered by
// the email regex and are not listed here.
const FREETEXT_CATS = ['company', 'domain', 'domainlabel', 'tenant', 'mspname'];
let knownRe = null;
let knownReVersion = -1;
let knownMap = new Map();

function knownRealRegex() {
  if (knownReVersion === freetextVersion) return knownRe;
  const entries = [];
  const map = new Map();
  for (const cat of FREETEXT_CATS) {
    const minLen = cat === 'domainlabel' ? 5 : 4;
    for (const [realNorm, fake] of fwd(cat)) {
      if (!realNorm || realNorm.length < minLen) continue;
      if (map.has(realNorm)) continue;
      entries.push(realNorm);
      map.set(realNorm, fake);
    }
  }
  entries.sort((a, b) => b.length - a.length);
  knownRe = entries.length ? new RegExp(entries.map(escapeRe).join('|'), 'gi') : null;
  knownMap = map;
  knownReVersion = freetextVersion;
  // A newly-known real value can change how an earlier string scrubs, so the
  // memo cache has to go with it (this fires ~once per tenant, not per string).
  scrubCache.clear();
  return knownRe;
}

const scrubCache = new Map();
const SCRUB_CACHE_MAX = 20000;

/**
 * Anonymize identifiers embedded in arbitrary text: emails, IPv4/IPv6, and any
 * real company name / domain / tenant GUID already seen this session.
 */
export function scrubFreeText(value) {
  if (typeof value !== 'string') return value;
  const s = value;
  if (s.length < 4 || s.length > 200000) return s;
  if (ISO_DATE_RE.test(s) || SID_RE.test(s)) return s;

  const cacheable = s.length <= 512;
  if (cacheable) {
    const hit = scrubCache.get(s);
    if (hit !== undefined) return hit;
  }

  let out = s;
  if (out.indexOf('@') !== -1) {
    out = out.replace(EMAIL_GLOBAL, (m) => {
      const f = fakeUpn(m);
      return typeof f === 'string' ? f : m;
    });
  }
  if (/\d/.test(out)) {
    out = out.replace(IPV4_FREE, (full, pre, ip) => (validOctets(ip) ? pre + maskIp(ip) : full));
  }
  if (out.indexOf(':') !== -1) {
    out = out.replace(IPV6_FREE, (m, offset, whole) => {
      // Skip candidates glued to surrounding word characters (`Type::Name`) and
      // anything without a hex digit — only real addresses should be masked.
      if (!/[0-9A-Fa-f]/.test(m)) return m;
      if (offset > 0 && WORDISH.test(whole[offset - 1])) return m;
      const after = whole[offset + m.length];
      if (after && WORDISH.test(after)) return m;
      const masked = maskIp(m);
      return typeof masked === 'string' ? masked : m;
    });
  }
  const re = knownRealRegex();
  if (re) {
    re.lastIndex = 0;
    out = out.replace(re, (m) => knownMap.get(norm(m)) ?? m);
  }

  if (cacheable) {
    if (scrubCache.size >= SCRUB_CACHE_MAX) scrubCache.clear();
    scrubCache.set(s, out);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Key registry
// ---------------------------------------------------------------------------

const keyCache = new Map();
function normKey(k) {
  let hit = keyCache.get(k);
  if (hit === undefined) {
    hit = String(k).replace(/[_\-\s.]/g, '').toLowerCase();
    keyCache.set(k, hit);
  }
  return hit;
}

const COMPANY_KEYS = new Set([
  'friendlyname', 'tenantname', 'tenantfriendlyname', 'companyname', 'clientname',
  'accountname', 'bpcaccountname', 'bpctenantname', 'organizationname', 'orgname',
]);

const DOMAIN_KEYS = new Set(['domain', 'domainname', 'tenantdomain', 'initialdomain', 'verifieddomains']);

const MSP_CODE_KEYS = new Set(['msp', 'mspcode', 'mspcodes', 'adminmspcodes', 'includedmspcodes', 'excludedmspcodes']);
const MSP_NAME_KEYS = new Set(['mspname', 'mspdisplayname', 'mspfriendlyname']);

const UPN_KEYS = new Set([
  'userprincipalname', 'upn', 'mail', 'email', 'emailaddress', 'emailrecipients', 'senderaddress',
  'recipientaddress', 'primaryaddress', 'primarysmtpaddress', 'smtpaddress', 'mailboxownerupn',
  'delegateupn', 'memberupn', 'actorupn', 'ownerupn', 'psaresourceemail', 'closedby', 'updatedby',
  'addedby', 'performedby', 'requestedby', 'checkedby', 'linkedby', 'disabledby', 'psaclosedby',
  'resolvedby', 'actorname', 'trustee', 'mailboxowner', 'targetupnssample', 'lastuser', 'lastusername',
  // identityResolution: every address a case searched for one person (v1.135.0).
  // typedUpn/primaryUpn are caught by the endsWith('upn') rule below.
  'identifiers',
]);

// These keys also carry GUIDs, SIDs and the literal 'Not Available' — only
// email-shaped values are transformed.
const CONDITIONAL_UPN_KEYS = new Set([
  'userid', 'user', 'actor', 'initiatedby', 'targetuser', 'member', 'owner', 'account',
  'signinuser', 'identity', 'targetaccount', 'principalname', 'caseupn',
]);

const PERSON_KEYS = new Set([
  'username', 'userdisplayname', 'actordisplayname', 'memberdisplayname', 'givenname', 'surname',
  'firstname', 'lastname', 'psaresourcename', 'ownerdisplayname',
]);

const IP_KEYS = new Set([
  'ipaddress', 'ip', 'clientip', 'clientipaddress', 'sourceip', 'sourceipaddress', 'callerip',
  'callipaddress', 'inferredip', 'originalipaddress', 'resolveduserip', 'remoteip', 'deviceip',
  'publicip', 'localip', 'matchedip', 'eventip', 'ipv4address', 'ipv6address', 'lastknownip',
]);

const IP_CSV_KEYS = new Set([
  'allresolvedips', 'signiniplist', 'iplist', 'ips', 'exactipsseen', 'serviceipsseen',
  'sourceips', 'distinctips', 'resolvedips',
]);

const TENANT_KEYS = new Set([
  'tenantid', 'organizationid', 'hometenantid', 'resourcetenantid', 'm365tenantid',
  'suppressiontenantid', 'aadtenantid', 'lookuptenantid', 'bpctenantid', 'tid',
]);

const TENANT_CSV_KEYS = new Set([
  'includedtenantids', 'excludedtenantids', 'sprayedtenantids', 'allowedtenantids',
  'admintenantids', 'tenantids',
]);

const SUBJECT_KEYS = new Set(['subject', 'flaggedsubject', 'emailsubject', 'messagesubject', 'subjects', 'itemsubject']);

const DEVICE_KEYS = new Set([
  'devicename', 'hostname', 'computername', 'machinename', 'devicedisplayname',
  'manageddevicename', 'deviceshortname', 'netbiosname',
]);

// Case fields whose object KEYS are IP addresses ({ "1.2.3.4": "safe" }). Only
// values are walked by default, so these need their keys masked too — otherwise
// per-IP classifications / comments / enrichment stop matching the masked rows
// they're rendered against.
const IP_KEYED_MAPS = new Set(['ipclassifications', 'ipcomments', 'ipenrichmentoverlay', 'ipevidence']);

const GROUP_KEYS = new Set(['groupdisplayname', 'groupname']);
const PHONE_KEYS = new Set(['phone', 'phonenumber', 'telephonenumber', 'mobilephone', 'businessphones']);
const SHORTNAME_KEYS = new Set(['shortname', 'tenantshortname']);

// Strings that must survive verbatim: functional request keys, non-identifying
// ids, enums, geo, product/rule names and version-bearing text.
const SKIP_KEYS = new Set([
  'rowkey', 'partitionkey', 'etag', 'timestamp', 'tabletimestamp',
  'blobpath', 'sourceeventsblobpath', 'resultsblobpath', 'pollurl', 'ticketurl', 'webhookurl',
  'operation', 'operationname', 'activitydisplayname', 'logtype', 'eventtype', 'status',
  'resultstatus', 'severity', 'rulename', 'rulemode', 'mode', 'category', 'family',
  'country', 'countryorregion', 'isocountry', 'countrycode', 'city', 'state', 'regionname',
  'useragent', 'version', 'appversion', 'osversion', 'agentversion', 'provider', 'networktype',
  'appdisplayname', 'appid', 'applicationid', 'applicationdisplayname', 'resourcedisplayname',
  'alertid', 'ruleid', 'eventid', 'deviceid', 'jobid', 'runid', 'correlationid', 'requestid',
  'sessionid', 'aadsessionid', 'objectid', 'entraobjectid', 'roletemplateid', 'legacyruleid',
  'pythonfunctionname', 'operatingsystem', 'os', 'trusttype', 'compliancestate',
  'psaticketid', 'psaticketnumber', 'kvsecretname', 'skuid', 'threatdisplayname',
  'hash', 'filehash', 'sha1', 'sha256', 'md5', 'filename', 'signer', 'prefixcidr',
  'networkaddress', 'matchkey', 'keykind', 'sourcefeed', 'clientinfostring',
]);
// NOTE: `caseId` is deliberately NOT skipped. Case IDs embed the investigated
// UPN (IR-20260223-user@client.com-001) and are rendered everywhere, so the
// embedded email is scrubbed by scrubFreeText and restored on the wire from the
// UPN key store (the fake email token round-trips inside the id).

function keyPresent(obj, normNames) {
  for (const k of Object.keys(obj)) if (normNames.includes(normKey(k))) return true;
  return false;
}

/**
 * `displayName` is overloaded across this API — disambiguate from siblings so a
 * device doesn't become a person and Microsoft app names stay readable.
 */
function displayNameTransformer(obj) {
  if (obj) {
    if (keyPresent(obj, ['deviceid', 'operatingsystem', 'osversion', 'trusttype', 'manageddeviceid',
      'azureaddeviceid', 'intunemanageddeviceid'])) return (v) => fakeDeviceName(v);
    if (keyPresent(obj, ['appid', 'applicationid', 'serviceprincipalid', 'appownertenantid',
      'publishername'])) return (v) => fakeAppName(v);
    if (keyPresent(obj, ['securityenabled', 'isassignabletorole', 'membercount',
      'grouptypes'])) return (v) => fakeGroupName(v);
    if (keyPresent(obj, ['mspcode']) && !keyPresent(obj, ['userprincipalname', 'upn'])) {
      return (v, o) => fakeMspName(v, o);
    }
    // MSP_CATALOG rows are exactly { code, displayName }.
    if (keyPresent(obj, ['code']) && Object.keys(obj).length <= 3) return (v, o) => fakeMspName(v, o);
  }
  return (v, o) => fakePersonName(v, o);
}

/** Mask the KEYS of an IP-keyed map, walking the values normally. */
function remapIpKeys(node) {
  if (!isPlainObject(node)) return anonymizeValue(node);
  const out = {};
  for (const k of Object.keys(node)) {
    const masked = maskIp(k);
    out[typeof masked === 'string' ? masked : k] = anonymizeValue(node[k]);
  }
  return out;
}

function transformIpKeyedMap(value) {
  if (typeof value === 'string') {
    const t = value.trim();
    if (!looksLikeJson(t)) return value;
    try { return JSON.stringify(remapIpKeys(JSON.parse(t))); } catch { return value; }
  }
  return remapIpKeys(value);
}

function transformerFor(nk, obj) {
  if (IP_KEYS.has(nk)) return (v) => maskIp(v);
  if (IP_CSV_KEYS.has(nk)) return (v) => mapCsv(v, maskIp);
  if (TENANT_KEYS.has(nk)) return (v) => maskTenantGuid(v);
  if (TENANT_CSV_KEYS.has(nk)) return (v) => mapCsv(v, maskTenantGuid);
  if (UPN_KEYS.has(nk) || (nk.length > 3 && nk.endsWith('upn'))) return (v, o) => mapCsv(v, fakeUpn, o);
  if (COMPANY_KEYS.has(nk)) return (v, o) => fakeTenantName(v, o);
  if (DOMAIN_KEYS.has(nk)) return (v, o) => fakeDomain(v, o);
  if (SHORTNAME_KEYS.has(nk)) return (v, o) => fakeShortName(v, o);
  if (MSP_CODE_KEYS.has(nk)) return (v, o) => mapCsv(v, fakeMspCode, o);
  if (MSP_NAME_KEYS.has(nk)) return (v, o) => fakeMspName(v, o);
  if (SUBJECT_KEYS.has(nk)) return (v) => fakeSubject(v);
  if (nk === 'itemattachments') return (v) => fakeAttachmentList(v);
  if (nk === 'itemfolderpath') return (v) => fakeFolderPath(v);
  if (nk === 'sourcefilename') return (v) => fakeFileName(v);
  // `filename` is CONTEXT-AWARE. It sits in SKIP_KEYS for the Defender file-hash
  // allowlist, where a malware file name genuinely IS non-identifying metadata —
  // hence its neighbours hash/sha256/signer/threatDisplayName. But normKey('FileName')
  // from a case event's Folders[].FileName normalizes to the SAME key, and the
  // SKIP_KEYS handler returns a string VERBATIM (skipping even free-text scrubbing).
  // So a live case screen-shared in Demo Mode showed real customer SharePoint /
  // OneDrive document names. Removing the key from SKIP_KEYS would break the
  // allowlist manager instead, so the containing object decides.
  if (nk === 'filename' && isCaseEventFileContext(obj)) return (v) => fakeFileName(v);
  if (DEVICE_KEYS.has(nk)) return (v) => fakeDeviceName(v);
  if (GROUP_KEYS.has(nk)) return (v) => fakeGroupName(v);
  if (PHONE_KEYS.has(nk)) return (v) => fakePhone(v);
  if (PERSON_KEYS.has(nk)) return (v, o) => fakePersonName(v, o);
  if (nk === 'caseid') return (v) => fakeCaseId(v);
  if (CONDITIONAL_UPN_KEYS.has(nk)) {
    // A non-email value can be the pipeline's "Domain Admins (S-1-5-21-…-512)" label, whose
    // domain SID identifies the client's AD domain.
    return (v, o) => (EMAIL_ANCHORED.test(String(v).trim()) ? fakeUpn(v, o)
      : anonymizeValue(typeof v === 'string' ? maskSids(v) : v));
  }
  if (nk === 'displayname') return displayNameTransformer(obj);
  // Directory-name resolver payloads: { name, type: 'group'|'user'|'role' } —
  // used by ResolveDirectoryObjects and the alert row's resolved-names map.
  // Directory ROLE names are Microsoft's own and stay readable.
  if (nk === 'name' && obj) {
    const typeKey = Object.keys(obj).find((k) => normKey(k) === 'type');
    const kind = typeKey ? norm(obj[typeKey]) : '';
    if (kind === 'group') return (v) => fakeGroupName(v);
    if (kind === 'user') return (v, o) => fakePersonName(v, o);
  }
  // MSP catalog row: { code: 'PFIT', displayName: 'Pretty Fly IT' }.
  if (nk === 'code' && obj && keyPresent(obj, ['displayname']) && Object.keys(obj).length <= 3) {
    return (v, o) => (/^[A-Za-z0-9]{2,6}$/.test(String(v).trim()) ? fakeMspCode(v, o) : v);
  }
  // Pipeline alert facts (alertFactsJson — ALERT-FACTS-CONTRACT.md). A fact is
  // { key, group, label, value } and its `key` says what the value IS, so a person or
  // a recipient list can be faked precisely. Free-text scrubbing alone fakes the email
  // addresses but leaves display names ("Maloney, Jessica", "John Smith <…>") verbatim.
  if (nk === 'value' && isAlertFactObject(obj) && ALERT_FACT_PEOPLE_KEY.test(obj.key)) {
    return (v, o) => fakeRecipientList(v, o);
  }
  // local_group_change (contract §4.4): the added member, its group's members, the actor and
  // the device name the people and the machine; SIDs carry the client's AD domain identifier.
  if (nk === 'value' && isAlertFactObject(obj)) {
    const t = LOCAL_GROUP_FACT_FAKERS[obj.key];
    if (t) return (v, o) => (typeof v === 'string' && v ? t(v, o) : v);
  }
  // Entra ID Protection unfamiliar properties (unfamiliarPropertiesJson, ALERT-FACTS-CONTRACT.md §10).
  // The Device property's value is "name · OS · trust type · device id"; the free-text scrub masks
  // IPs and emails but would leave the hostname, so fake the first segment and scrub the rest.
  if (nk === 'value' && obj && obj.reason === 'UnfamiliarDevice' && keyPresent(obj, ['property'])) {
    return (v) => {
      if (typeof v !== 'string' || !v) return v;
      const [first, ...rest] = v.split(' · ');
      return [fakeDeviceName(first), ...rest.map((s) => scrubFreeText(s))].join(' · ');
    };
  }
  // TenantIPsubnet's value is the user's /24 or /64 ("3.223.153.0/24"). The free-text IPv4 pattern
  // deliberately skips anything followed by "/", so mask the address half like any other IP.
  if (nk === 'value' && obj && obj.reason === 'UnfamiliarTenantIPsubnet' && keyPresent(obj, ['property'])) {
    return (v) => {
      if (typeof v !== 'string' || !v.includes('/')) return scrubFreeText(v);
      const [addr, len] = v.split('/');
      const masked = maskIp(addr);
      return `${typeof masked === 'string' ? masked : addr}/${len}`;
    };
  }
  // The headline is free text that repeats the fact values (sender names, rule name),
  // and there is no structure to fake it by — so Demo Mode drops it. The facts table
  // still renders; parseAlertFacts treats an empty headline as absent.
  // (keyPresent is ANY-of, so both keys are tested separately to require the facts shape.)
  if (nk === 'headline' && obj && keyPresent(obj, ['kind']) && keyPresent(obj, ['facts'])) {
    return () => '';
  }
  return null;
}

// Fact keys whose value names people or mailboxes: the rule's mailbox, sender/recipient
// conditions and exceptions, and every forward/redirect/delegate action. A trailing
// `.2`, `.3` is the contract's suffix for a repeated action type.
const ALERT_FACT_PEOPLE_KEY =
  /^(rule\.mailbox|condition\.(From|SentTo)|exception\.ExceptIf(From|SentTo)|action\.(Forward|Redirect|ForwardAsAttachment|Delegate|ForwardTo|RedirectTo|ForwardAsAttachmentTo))(\.\d+)?$/;

function isAlertFactObject(obj) {
  return !!obj && typeof obj.key === 'string' && keyPresent(obj, ['key', 'group', 'value'])
    && /^(rule|condition|exception|action|change|member|actor)\./.test(obj.key);
}

// A domain or machine SID: S-1-5-21-<a>-<b>-<c>-<RID>. The three sub-authorities identify the
// client's AD domain (or the PC), so they are masked; the RID is kept because a well-known one
// (-512 Domain Admins, -513 Domain Users) is what the reader needs. Built-in SIDs (S-1-5-32-544)
// and anything else pass through.
const DOMAIN_SID = /S-1-5-21-\d+-\d+-\d+(-\d+)?/g;
function maskSids(text) {
  return String(text).replace(DOMAIN_SID, (_m, rid) => `S-1-5-21-****-****-****${rid || ''}`);
}

// Member names the pipeline gives that are a role or a well-known built-in group, not a person.
const WELL_KNOWN_MEMBER = /^(Domain Admins|Domain Users|Domain Guests|Domain Computers|Enterprise Admins|Schema Admins|Administrators|Users|Guests|Remote Desktop Users|Power Users|Backup Operators|Authenticated Users|Everyone|INTERACTIVE|SYSTEM|NETWORK SERVICE|LOCAL SERVICE)$/i;

function fakeLocalGroupMember(value, obj) {
  const v = value.trim();
  if (/^S-1-/.test(v)) return maskSids(v);
  if (WELL_KNOWN_MEMBER.test(v) || /\(Entra role/.test(v)) return maskSids(v);
  if (EMAIL_ANCHORED.test(v)) return fakeUpn(v, obj);
  const dn = v.match(/^([^\\]+)\\(.+)$/);
  if (dn) return `${fakeShortName(dn[1], obj)}\\${fakePersonName(dn[2], obj)}`;
  return fakePersonName(v, obj);
}

// `member.group_members`: `; `-separated UPNs and `Name (group)` entries, then `… and N more`,
// or one of the contract's fixed sentences (None / Not available / Not checked).
function fakeGroupMemberList(value, obj) {
  if (/^(None|Not available|Not checked)\b/.test(value.trim())) return value;
  return value.split(/;\s*/).map((entry) => {
    const e = entry.trim();
    if (!e || /^… and \d+ more$/.test(e)) return e;
    const g = e.match(/^(.*) \(group\)$/);
    if (g) return `${WELL_KNOWN_MEMBER.test(g[1]) ? g[1] : fakeGroupName(g[1])} (group)`;
    return fakeLocalGroupMember(e, obj);
  }).join('; ');
}

// `actor.name`: `<name> (local account on <device>)`, `<name> (Entra ID account)`, `DOMAIN\\name`
// or a UPN.
function fakeLocalGroupActor(value, obj) {
  const local = value.match(/^(.*) \(local account on (.+)\)$/);
  if (local) return `${fakePersonName(local[1], obj)} (local account on ${fakeDeviceName(local[2])})`;
  const entra = value.match(/^(.*) \(Entra ID account\)$/);
  if (entra) return `${fakeLocalGroupMember(entra[1], obj)} (Entra ID account)`;
  return fakeLocalGroupMember(value, obj);
}

const LOCAL_GROUP_FACT_FAKERS = {
  'member.name': fakeLocalGroupMember,
  'member.sid': (v) => maskSids(v),
  'member.group_members': fakeGroupMemberList,
  'member.domain': (v) => v.replace(/\(([^)]+)\)$/, (_m, d) => `(${fakeDeviceName(d)})`),
  'actor.name': fakeLocalGroupActor,
  'actor.sid': (v) => maskSids(v),
  'change.device': (v) => fakeDeviceName(v),
};

/** Fake a contract recipient list: entries are `; `-separated, each an address, a
 *  display name, or `Name <address>`. Each part is faked with the SAME mappers the rest
 *  of Demo Mode uses, so a person reads the same here as in the alert's user column. */
function fakeRecipientList(value, obj) {
  return String(value).split(/;\s*/).map((entry) => {
    const e = entry.trim();
    if (!e) return e;
    const m = e.match(/^(.*?)\s*<([^>]+)>$/);
    if (m) return `${m[1] ? fakePersonName(m[1], obj) : ''} <${fakeUpn(m[2], obj)}>`.trim();
    if (EMAIL_ANCHORED.test(e)) return fakeUpn(e, obj);
    return fakePersonName(e, obj);
  }).join('; ');
}

// Keys that are in SKIP_KEYS but whose meaning depends on the object around them.
// transformerFor() is consulted FIRST for these, so a context match can override the
// verbatim passthrough; no match still falls through to it.
const CONTEXT_AWARE_SKIP_KEYS = new Set(['filename']);

/** Is this `filename` a CASE EVENT's file name (sensitive) rather than a Defender
 *  file-hash allowlist row (non-identifying malware metadata)?
 *
 *  Positive test on the allowlist shape, not on the case shape: the allowlist row is
 *  the narrow, stable, known one, so anything else is treated as a case event. That
 *  is the safe default — an unrecognised container gets FAKED rather than passed
 *  through, so a future payload carrying file names cannot leak by omission. */
function isCaseEventFileContext(obj) {
  if (!obj) return true;
  return !keyPresent(obj, ['hashtype', 'threatdisplayname', 'sha256', 'sha1', 'md5', 'filehash', 'signer']);
}

function transformKeyed(key, value, obj) {
  const nk = normKey(key);
  if (SKIP_KEYS.has(nk) && !CONTEXT_AWARE_SKIP_KEYS.has(nk)) {
    return (typeof value === 'string') ? value : anonymizeValue(value);
  }
  if (SKIP_KEYS.has(nk)) {
    const ct = transformerFor(nk, obj);
    if (ct) return (typeof value === 'string') ? ct(value, obj) : anonymizeValue(value);
    return (typeof value === 'string') ? value : anonymizeValue(value);
  }
  if (IP_KEYED_MAPS.has(nk)) return transformIpKeyedMap(value);
  const t = transformerFor(nk, obj);
  if (t) {
    if (typeof value === 'string') return t(value, obj);
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === 'string' ? t(v, obj) : anonymizeValue(v)));
    }
    return anonymizeValue(value);
  }
  return anonymizeValue(value);
}

// ---------------------------------------------------------------------------
// Walker
// ---------------------------------------------------------------------------

function looksLikeJson(s) {
  if (s.length < 2) return false;
  const c = s.charCodeAt(0);
  return c === 123 /* { */ || c === 91 /* [ */;
}

function anonymizeValue(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    const t = value.trim();
    if (looksLikeJson(t)) {
      let parsed;
      try { parsed = JSON.parse(t); } catch { parsed = undefined; }
      if (parsed !== null && typeof parsed === 'object') {
        return JSON.stringify(anonymizeDeep(parsed));
      }
    }
    return scrubFreeText(value);
  }
  if (typeof value === 'object') return anonymizeDeep(value);
  return value;
}

/** Recursively anonymize a parsed JSON value IN PLACE; returns the same node. */
export function anonymizeDeep(node) {
  if (node === null || node === undefined) return node;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) node[i] = anonymizeValue(node[i]);
    return node;
  }
  if (isPlainObject(node)) {
    for (const k of Object.keys(node)) node[k] = transformKeyed(k, node[k], node);
    return node;
  }
  return anonymizeValue(node);
}

/** Entry point used by api/client.js on every parsed response body. */
export function anonymizeApiResponse(body) {
  if (body === null || body === undefined) return body;
  const out = anonymizeDeep(body);
  if (LEAK_CHECK) leakCheck(out);
  return out;
}

// ---------------------------------------------------------------------------
// Outbound restore — masked/fake request keys → real values
// ---------------------------------------------------------------------------

function formEncode(token) {
  try { return new URLSearchParams({ v: token }).toString().slice(2); } catch { return token; }
}

/**
 * Pair each encoded form of the FAKE token with the matching encoded form of the
 * REAL value, so a percent-encoded match is replaced by a percent-encoded real
 * value (`…%40contoso.com` → `…%40cohenusa.com`, never a raw `@` in a query).
 */
function encodedPairs(fake, real) {
  const pairs = [[fake, real]];
  try { pairs.push([encodeURIComponent(fake), encodeURIComponent(real)]); } catch { /* ignore */ }
  pairs.push([formEncode(fake), formEncode(real)]);
  const seen = new Set();
  return pairs.filter(([f]) => {
    if (!f || seen.has(f)) return false;
    seen.add(f);
    return true;
  });
}

let restorePlan = null;
let restorePlanVersion = -1;

function buildRestorePlan() {
  const plan = [];
  const addExact = (fake, real) => {
    if (!fake || !real || norm(fake) === norm(real)) return;
    for (const [fakeForm, realForm] of encodedPairs(fake, real)) {
      plan.push({ re: new RegExp(escapeRe(fakeForm), 'gi'), real: realForm });
    }
  };

  // Longest tokens first so nothing gets partially consumed.
  for (const [fakeNorm, real] of Array.from(rev('tenant')).sort((a, b) => b[0].length - a[0].length)) {
    addExact(fakeNorm, real);
  }
  for (const [maskedNorm, reals] of ipReverse) {
    if (reals.size !== 1) continue; // ambiguous mask — leave the call un-restored
    addExact(maskedNorm, Array.from(reals)[0]);
  }
  for (const [fakeNorm, real] of rev('upn')) addExact(fakeNorm, real);

  // MSP codes are short tokens — require non-alphanumeric boundaries.
  for (const [fakeNorm, real] of rev('msp')) {
    if (!fakeNorm || fakeNorm === norm(real)) continue;
    plan.push({
      re: new RegExp(`([^A-Za-z0-9]|^)(${escapeRe(fakeNorm)})(?![A-Za-z0-9])`, 'gi'),
      real,
      bounded: true,
    });
  }

  restorePlan = plan;
  restorePlanVersion = storeVersion;
  return plan;
}

function restoreString(s) {
  if (typeof s !== 'string' || !s) return s;
  const plan = (restorePlanVersion === storeVersion && restorePlan) ? restorePlan : buildRestorePlan();
  let out = s;
  for (const entry of plan) {
    entry.re.lastIndex = 0;
    out = entry.bounded
      ? out.replace(entry.re, (_m, pre) => pre + entry.real)
      : out.replace(entry.re, entry.real);
  }
  return out;
}

/**
 * Restore real values in an outbound request path + query string. Handles raw
 * and URL-encoded token forms (`URLSearchParams` percent-encodes `*` as `%2A`
 * and `@` as `%40`).
 */
export function restoreEndpoint(endpoint) {
  return restoreString(endpoint);
}

/** Restore real values inside a JSON-stringified request body (read-only POSTs). */
export function restoreBody(bodyString) {
  return restoreString(bodyString);
}

/** True when a masked IP maps back to exactly one real IP (restorable). */
export function isRestorableIp(maskedIp) {
  const set = ipReverse.get(norm(maskedIp));
  return !!set && set.size === 1;
}

// ---------------------------------------------------------------------------
// Dev leak assertion — console.warn only, never throws
// ---------------------------------------------------------------------------

const warned = new Set();
const HARDCODED_LEAKS = ['bluealliance.com', 'bluealliancemember.onmicrosoft.com'];
const LEAK_CATS = ['company', 'domain', 'tenant', 'mspname', 'upn'];
let leakRe = null;
let leakReVersion = -1;

function leakRegex() {
  if (leakReVersion === storeVersion) return leakRe;
  const seen = new Set();
  for (const cat of LEAK_CATS) {
    for (const realNorm of fwd(cat).keys()) {
      if (realNorm && realNorm.length >= 6) seen.add(realNorm);
    }
  }
  for (const bad of HARDCODED_LEAKS) seen.add(bad);
  const parts = Array.from(seen).sort((a, b) => b.length - a.length).map(escapeRe);
  leakRe = parts.length ? new RegExp(parts.join('|'), 'gi') : null;
  leakReVersion = storeVersion;
  return leakRe;
}

function warnOnce(kind, sample) {
  const key = `${kind}:${sample}`;
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(`[DemoMode] possible leak (${kind}): ${sample}`);
}

function leakCheck(body) {
  let text;
  try { text = JSON.stringify(body); } catch { return; }
  if (!text || text.length > 4000000) return;

  const re = leakRegex();
  if (re) {
    re.lastIndex = 0;
    const hit = re.exec(text);
    if (hit) warnOnce('real-value-survived', hit[0]);
  }
  // Post-mask every legitimate IPv4 renders as **.**.x.y, so a full dotted quad
  // means a field the key registry missed (version strings fail octet checks).
  IPV4_FREE.lastIndex = 0;
  let m;
  while ((m = IPV4_FREE.exec(text)) !== null) {
    if (validOctets(m[2])) { warnOnce('unmasked-ipv4', m[2]); break; }
  }
  IPV4_FREE.lastIndex = 0;
}

// ---------------------------------------------------------------------------
// Test surface
// ---------------------------------------------------------------------------

/** Reset all session state. Test-only. */
export function __resetDemoStore() {
  forward.clear();
  reverse.clear();
  ipReverse.clear();
  companyIdx.clear();
  idxOwner.clear();
  mspIdx.clear();
  mspIdxOwner.clear();
  scrubCache.clear();
  keyCache.clear();
  warned.clear();
  knownReVersion = -1;
  leakReVersion = -1;
  restorePlanVersion = -1;
  storeVersion++;
  freetextVersion++;
}
